import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Loader2,
  Package,
  PenLine,
  RefreshCw,
  Sparkles,
  Trash2,
  Truck,
  XCircle,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import {
  OpCallout,
  OpNowBar,
  OpPrimaryField,
  OpRoleBadge,
  OpSection,
  OpStepRail,
  OpAiJobModalDemoTab,
  OpPrzetwarzanieDemo,
  OpSlideEditDemo,
  OP_SURFACE,
  OP_SURFACE_FAMILIES,
  type OpStepItem,
} from "@/components/op";
import { StudioNowBar, StudioSection } from "@/components/studioVoice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type { StudioNowAction } from "@/lib/studioVoice";

const WEAK_NOW: StudioNowAction = {
  tab: "brief",
  label: "Wklej szkic, potem Zredaguj",
  detail: "To jest słaby wariant (StudioNowBar) do porównania.",
  sectionId: "demo-primary",
};

const DEMO_STEPS_IDLE: OpStepItem[] = [
  { id: "brief", label: "Wejście", detail: "Szkic gotowy", state: "done", percent: 100 },
  { id: "copy", label: "Przetwarzanie", detail: "Czeka na Zredaguj", state: "waiting", percent: 0 },
  { id: "render", label: "Wyjście", detail: "Zablokowane", state: "blocked", percent: 0 },
  { id: "publish", label: "Publikacja", detail: "Zablokowane", state: "blocked", percent: 0 },
];

const DEMO_STEPS_RUNNING: OpStepItem[] = [
  { id: "brief", label: "Wejście", detail: "Szkic gotowy", state: "done", percent: 100 },
  { id: "copy", label: "Przetwarzanie", detail: "LLM redaguje ściany", state: "active", percent: 42 },
  { id: "render", label: "Wyjście", detail: "Czeka", state: "waiting", percent: 0 },
  { id: "publish", label: "Publikacja", detail: "Czeka", state: "waiting", percent: 0 },
];

const DEMO_STEPS_MIXED: OpStepItem[] = [
  { id: "a", label: "Done", state: "done", percent: 100 },
  { id: "b", label: "Active", state: "active", percent: 55, detail: "W toku" },
  { id: "c", label: "Waiting", state: "waiting" },
  { id: "d", label: "Error", state: "error", detail: "Timeout" },
  { id: "e", label: "Blocked", state: "blocked" },
];

const SHIP_STEPS: OpStepItem[] = [
  { id: "match", label: "Dopasuj SKU", state: "done", percent: 100 },
  { id: "quote", label: "Wycena", state: "done", percent: 100 },
  { id: "label", label: "Etykieta", state: "active", percent: 60, detail: "Furgonetka…" },
  { id: "pickup", label: "Odbiór", state: "waiting" },
];

function ExampleLabel({ children }: { children: string }) {
  return (
    <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">{children}</p>
  );
}

function Chip({ active, children }: { active?: boolean; children: string }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-md border px-2.5 py-1 text-xs font-medium",
        active ? "border-brand bg-brand/15 text-brand" : "border-slate-300 bg-white text-slate-700",
      )}
    >
      {children}
    </span>
  );
}

function LoudCatalog({ draft, setDraft }: { draft: string; setDraft: (v: string) => void }) {
  return (
    <div className="space-y-8">
      <section>
        <ExampleLabel>1 · Sticky TERAZ</ExampleLabel>
        <OpNowBar
          label="Wklej szkic, potem kliknij główny klawisz"
          detail="Sticky pasek TERAZ. Tu patrzysz najpierw."
          actionLabel="Pokaż pole"
          onAction={() => document.getElementById("demo-primary")?.scrollIntoView({ behavior: "smooth" })}
        />
      </section>

      <section>
        <ExampleLabel>2 · Krok główny + pole + CTA</ExampleLabel>
        <OpSection role="now" step={1} id="demo-primary" title="Start koncepcji" hint="Główna ścieżka na tym ekranie.">
          <OpPrimaryField
            label="Główne pole · wklej szkic"
            hint="Gruba ramka brand = tu wpisujesz."
            placeholder={"Kiedyś…\nTeraz…\n1. …"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="brand" size="lg">
              <PenLine className="mr-2 h-4 w-4" />
              Zredaguj slajdy
            </Button>
            <Button type="button" variant="outline" size="lg">
              Anuluj
            </Button>
          </div>
        </OpSection>
      </section>

      <section>
        <ExampleLabel>3 · Krok 2 (ścieżka, bez brand field)</ExampleLabel>
        <OpSection role="path" step={2} title="Wybór koncepcji" hint="Drugi obowiązkowy krok (przykład).">
          <p className="text-sm text-slate-700">
            Tu byłaby siatka kart. Ten blok ma numer i pasek, ale bez brand ring na polu.
          </p>
          <Button type="button" variant="outline">
            Wybierz kartę
          </Button>
        </OpSection>
      </section>

      <section className="space-y-3">
        <ExampleLabel>4 · Opcjonalne vs dodatkowe</ExampleLabel>
        <OpSection role="optional" title="Parametry" hint="Nie blokuje ścieżki. Domyślnie zwinięte i szare.">
          <div className="flex flex-wrap gap-1.5">
            <Chip active>Ból: relacja z matką</Chip>
            <Chip>Styl: prosto</Chip>
            <Chip>Funnel: MOFU</Chip>
          </div>
        </OpSection>
        <OpSection role="advanced" title="Narzędzia lab">
          <p className="text-sm text-slate-700">Rzadkie akcje. Jeszcze niżej niż opcjonalne.</p>
        </OpSection>
      </section>

      <section>
        <ExampleLabel>5 · Callouty (wszystkie warianty)</ExampleLabel>
        <div className="grid gap-3 md:grid-cols-2">
          <OpCallout variant="help" title="Hint przy polu">
            Pomoc = slate + Info. Nigdy brand.
          </OpCallout>
          <OpCallout variant="process" title="LLM / kurier">
            Proces w toku. Loader2 + brand.
          </OpCallout>
          <OpCallout variant="success" title="Wysłano">
            Done = success (zielony token), nie brand.
          </OpCallout>
          <OpCallout variant="warning" title="Sandbox">
            Uwaga operacyjna.
          </OpCallout>
          <OpCallout variant="destructive" title="Brak SKU">
            Blokuje dalszy krok.
          </OpCallout>
        </div>
      </section>

      <section>
        <ExampleLabel>6 · Rail procesu (stany mieszane)</ExampleLabel>
        <OpStepRail title="OpStepRail · stany" now="Przykład mieszany" steps={DEMO_STEPS_MIXED} />
      </section>
    </div>
  );
}

function WeakCatalog({ draft, setDraft }: { draft: string; setDraft: (v: string) => void }) {
  return (
    <div className="space-y-4">
      <StudioNowBar action={WEAK_NOW} currentTab="brief" onGo={() => undefined} />
      <StudioSection
        role="next"
        id="demo-primary-weak"
        title="Start koncepcji"
        hint="Słaby wariant: brand/5 i badge 10px."
      >
        <label className="text-xs font-medium text-slate-600">Wklej szkic</label>
        <textarea
          className="mt-1 min-h-[140px] w-full rounded-md border border-input bg-transparent p-2 text-sm"
          placeholder={"Kiedyś…\nTeraz…\n1. …"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" variant="brand" size="sm">
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            Zredaguj slajdy
          </Button>
          <Button type="button" variant="outline" size="sm">
            Anuluj
          </Button>
        </div>
      </StudioSection>
      <StudioSection role="optional" title="Parametry" hint="Dashed slate, łatwo zlewa się z resztą.">
        <div className="flex flex-wrap gap-1.5">
          <Chip active>Ból</Chip>
          <Chip>Styl</Chip>
        </div>
      </StudioSection>
      <p className="rounded-md border border-border bg-muted/40 p-2 text-[11px] text-muted-foreground">
        Help strip (muted) - prawie niewidoczny obok karty.
      </p>
    </div>
  );
}

function ButtonsAndFields() {
  return (
    <div className="space-y-8">
      <section>
        <ExampleLabel>Drabinka przycisków (1 viewport = 1 brand)</ExampleLabel>
        <div className="rounded-xl border-2 border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="brand" size="lg">
              Główny CTA
            </Button>
            <Button type="button" variant="outline" size="lg">
              Secondary
            </Button>
            <Button type="button" variant="ghost" size="lg">
              Tertiary
            </Button>
            <Button type="button" variant="destructive" size="lg">
              <Trash2 className="mr-2 h-4 w-4" />
              Usuń
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            brand = jedyna akcja ścieżki · outline = odśwież/anuluj · ghost = pin/link · destructive = kasowanie
          </p>
        </div>
      </section>

      <section>
        <ExampleLabel>Źle: trzy brand obok siebie</ExampleLabel>
        <div className="rounded-xl border-2 border-dashed border-destructive/40 bg-destructive/5 p-4">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="brand">
              Zapisz
            </Button>
            <Button type="button" variant="brand">
              Wyślij
            </Button>
            <Button type="button" variant="brand">
              Publikuj
            </Button>
          </div>
          <p className="mt-2 text-xs text-destructive">Mózg nie wie, który jest „ten”. Unikaj.</p>
        </div>
      </section>

      <section>
        <ExampleLabel>Pole główne vs pole zwykłe</ExampleLabel>
        <div className="grid gap-4 lg:grid-cols-2">
          <OpPrimaryField
            label="Główne pole"
            hint="border-2 border-brand, text-base, min 180px"
            defaultValue="Tu wpisujesz główny input."
          />
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Pole zwykłe / pomocnicze</label>
            <Input placeholder="np. notatka wewnętrzna" defaultValue="" />
            <p className="text-xs text-muted-foreground">Zwykły Input: cienka ramka, nie konkuruje z głównym.</p>
            <label className="mt-3 block text-sm font-medium text-slate-700">Drugi pomocniczy</label>
            <Input placeholder="SKU / tracking" />
          </div>
        </div>
      </section>

      <section>
        <ExampleLabel>Ikony akcji (outline / ghost), nie brand</ExampleLabel>
        <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-4">
          <Button type="button" variant="outline" size="sm">
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Odśwież
          </Button>
          <Button type="button" variant="outline" size="sm">
            <Copy className="mr-2 h-3.5 w-3.5" />
            Kopiuj
          </Button>
          <Button type="button" variant="ghost" size="icon" aria-label="Usuń">
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button type="button" variant="brand" size="sm" disabled>
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            W toku…
          </Button>
        </div>
      </section>

      <section>
        <ExampleLabel>Badge statusowe (semafor: zieleń = zatwierdzone)</ExampleLabel>
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-col items-start gap-1">
            <Badge variant="brand">w toku</Badge>
            <span className="text-[10px] text-slate-500">proces / CTA</span>
          </div>
          <div className="flex flex-col items-start gap-1">
            <Badge variant="success">wybrane / wysłane</Badge>
            <span className="text-[10px] text-slate-500">zielone światło</span>
          </div>
          <div className="flex flex-col items-start gap-1">
            <Badge variant="warning">sandbox / uwaga</Badge>
            <span className="text-[10px] text-slate-500">uwaga</span>
          </div>
          <div className="flex flex-col items-start gap-1">
            <Badge variant="destructive">reklamacja</Badge>
            <span className="text-[10px] text-slate-500">problem</span>
          </div>
          <div className="flex flex-col items-start gap-1">
            <Badge variant="secondary">batch</Badge>
            <span className="text-[10px] text-slate-500">meta</span>
          </div>
          <div className="flex flex-col items-start gap-1">
            <Badge variant="outline">oczekuje</Badge>
            <span className="text-[10px] text-slate-500">neutral</span>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatusListExamples() {
  const rows = [
    { id: "1", name: "Anna K.", status: "success" as const, label: "Wysłane", detail: "InPost · dziś 11:02" },
    { id: "2", name: "Marta W.", status: "process" as const, label: "Etykieta", detail: "Furgonetka…" },
    { id: "3", name: "Ola P.", status: "warning" as const, label: "Do dopłaty", detail: "Brak 12 zł" },
    { id: "4", name: "Ewa L.", status: "destructive" as const, label: "Reklamacja", detail: "Uszkodzenie" },
    { id: "5", name: "Zofia R.", status: "secondary" as const, label: "Batch", detail: "3 książki" },
  ];

  return (
    <div className="space-y-8">
      <section>
        <ExampleLabel>Lista operatora: status Badge, nie kolor całego wiersza</ExampleLabel>
        <div className="overflow-hidden rounded-xl border-2 border-slate-200 bg-white shadow-sm">
          <div className="grid grid-cols-[1fr_auto_1.2fr] gap-3 border-b bg-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <span>Zamówienie</span>
            <span>Status</span>
            <span>Szczegół</span>
          </div>
          {rows.map((row) => (
            <div
              key={row.id}
              className="grid grid-cols-[1fr_auto_1.2fr] items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
            >
              <div>
                <p className="text-sm font-semibold text-slate-900">{row.name}</p>
                <p className="text-xs text-muted-foreground">#{1000 + Number(row.id)}</p>
              </div>
              <Badge
                variant={
                  row.status === "process"
                    ? "brand"
                    : row.status === "secondary"
                      ? "secondary"
                      : row.status
                }
              >
                {row.status === "process" ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {row.label}
                  </span>
                ) : (
                  row.label
                )}
              </Badge>
              <p className="text-sm text-slate-700">{row.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <ExampleLabel>Źle: tęcza wierszy (sky / violet / rose) = wszystko „ważne”</ExampleLabel>
        <div className="space-y-2 rounded-xl border-2 border-dashed border-destructive/40 bg-destructive/5 p-3">
          <div className="rounded-md border border-sky-500/50 bg-sky-500/15 px-3 py-2 text-sm text-sky-900">
            Dopasowanie SKU (sky)
          </div>
          <div className="rounded-md border border-violet-500/50 bg-violet-500/15 px-3 py-2 text-sm text-violet-900">
            Batch 3 książki (violet)
          </div>
          <div className="rounded-md border border-rose-500/50 bg-rose-500/15 px-3 py-2 text-sm text-rose-900">
            Reklamacja (rose)
          </div>
          <p className="text-xs text-destructive">Tak wygląda Wysyłka dziś. Na OVL: Badge + zwykły wiersz.</p>
        </div>
      </section>

      <section className="grid gap-3 md:grid-cols-2">
        <div>
          <ExampleLabel>Gate blokujący</ExampleLabel>
          <OpCallout variant="destructive" title="Brak telefonu">
            Nie da się wycenić. Uzupełnij telefon UK/DE, potem Wycena.
          </OpCallout>
        </div>
        <div>
          <ExampleLabel>Sukces po akcji</ExampleLabel>
          <OpCallout variant="success" title="Etykieta OK">
            PDF zapisany. Możesz przejść do odbioru.
          </OpCallout>
        </div>
      </section>
    </div>
  );
}

function BriefMock({
  draft,
  setDraft,
  jobRunning,
}: {
  draft: string;
  setDraft: (v: string) => void;
  jobRunning: boolean;
}) {
  const steps = useMemo(() => (jobRunning ? DEMO_STEPS_RUNNING : DEMO_STEPS_IDLE), [jobRunning]);

  return (
    <div className="grid gap-4 xl:grid-cols-12">
      <div className="space-y-4 xl:col-span-8">
        <OpNowBar
          label="Wklej szkic, potem Zredaguj"
          detail="Makieta Brief bez API. Oceń język w gęstości prawdziwego ekranu."
          actionLabel="Do pola"
          onAction={() => document.getElementById("brief-step-1")?.scrollIntoView({ behavior: "smooth" })}
        />

        {jobRunning ? (
          <OpCallout variant="process" title="Zredaguj slajdy">
            Job w toku (42%). Pasek po prawej też pulsuje.
          </OpCallout>
        ) : null}

        <OpSection role="now" step={1} id="brief-step-1" title="Szkic na ściany" hint="Źródło ścieżki. Redakcja idzie stąd.">
          <OpPrimaryField
            label="Główne pole · wklej szkic"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={"Kiedyś bałam się, że będę jak moja matka.\nTeraz…"}
          />
          <Button type="button" variant="brand" size="lg" className="w-full sm:w-auto" disabled={jobRunning}>
            <PenLine className="mr-2 h-4 w-4" />
            {jobRunning ? "Przetwarzam…" : "Zredaguj slajdy"}
          </Button>
          <Button type="button" variant="outline" size="lg" className="ml-0 mt-2 sm:ml-2 sm:mt-0">
            Nowy czasotekst
          </Button>
        </OpSection>

        <OpSection role="path" step={2} title="Wybór koncepcji" hint="Po jobie: wybierz kartę (przykład drugiego kroku).">
          <div className="grid gap-2 sm:grid-cols-2">
            {["A · Hak o lęku", "B · Scena przy stole", "C · Lista granic", "D · List do córki"].map((t, i) => (
              <button
                key={t}
                type="button"
                className={cn(
                  "rounded-lg border-2 px-3 py-3 text-left text-sm",
                  i === 0 ? "border-brand bg-brand/10 font-semibold" : "border-slate-200 bg-white",
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </OpSection>

        <OpSection role="optional" title="Parametry" hint="System dopasuje ze szkicu, jeśli nic nie klikniesz.">
          <div className="space-y-3">
            <div>
              <p className="mb-1.5 text-xs font-semibold text-slate-700">Ból</p>
              <div className="flex flex-wrap gap-1.5">
                <Chip active>Relacja z matką</Chip>
                <Chip>Lęk przed powtórzeniem</Chip>
                <Chip>Granice</Chip>
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold text-slate-700">Styl</p>
              <div className="flex flex-wrap gap-1.5">
                <Chip>Prosto</Chip>
                <Chip active>Ciepło</Chip>
                <Chip>Ostro</Chip>
              </div>
            </div>
          </div>
        </OpSection>

        <OpSection role="advanced" title="Inne starty" hint="Nie blokują głównej ścieżki.">
          <Button type="button" variant="outline" size="sm">
            Import z IG
          </Button>
        </OpSection>

        <OpCallout variant="help">
          Tip: opcjonalne ma być niewidoczne, dopóki go nie otworzysz. CTA ma krzyczeć brandem.
        </OpCallout>
      </div>

      <div className="xl:col-span-4">
        <OpStepRail
          className="xl:sticky xl:top-4"
          now={jobRunning ? "Redaguję ściany…" : "Wybierz brief albo wklej szkic."}
          steps={steps}
        />
      </div>
    </div>
  );
}

function WysylkaMock({ jobRunning }: { jobRunning: boolean }) {
  return (
    <div className="grid gap-4 xl:grid-cols-12">
      <div className="space-y-4 xl:col-span-8">
        <OpNowBar
          label="Uzupełnij telefon, potem Wycena"
          detail="Makieta Wysyłki. Status = Badge, nie kolor całego wiersza."
          actionLabel="Do telefonu"
          onAction={() => document.getElementById("ship-step-1")?.scrollIntoView({ behavior: "smooth" })}
        />

        <OpCallout variant="warning" title="Sandbox Furgonetka">
          Tryb testowy. Etykiety nie idą na produkcję.
        </OpCallout>

        {jobRunning ? (
          <OpCallout variant="process" title="Generuję etykietę">
            Job kuriera w toku. Nie klikaj drugi raz.
          </OpCallout>
        ) : null}

        <OpSection role="now" step={1} id="ship-step-1" title="Dane odbiorcy" hint="Bez telefonu nie ma wyceny.">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <label className="text-sm font-semibold text-slate-900">Telefon (główne pole ścieżki)</label>
              <Input
                className="border-2 border-brand text-base shadow-sm focus-visible:ring-brand"
                placeholder="+44 … / +49 …"
                defaultValue="+48 500 000 000"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Imię i nazwisko</label>
              <Input defaultValue="Anna Kowalska" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Miasto</label>
              <Input defaultValue="Warszawa" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="brand" size="lg" disabled={jobRunning}>
              <Truck className="mr-2 h-4 w-4" />
              {jobRunning ? "Przetwarzam…" : "Wycena + etykieta"}
            </Button>
            <Button type="button" variant="outline" size="lg">
              <RefreshCw className="mr-2 h-4 w-4" />
              Odśwież
            </Button>
          </div>
        </OpSection>

        <OpSection role="path" step={2} title="SKU w paczce" hint="Potwierdź pozycje przed etykietą.">
          <div className="space-y-2">
            {[
              { title: "Boję się, że będę jak moja matka", qty: 1 },
              { title: "Ebook · pakiet digital", qty: 1 },
            ].map((line) => (
              <div
                key={line.title}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
              >
                <span className="text-sm font-medium text-slate-900">{line.title}</span>
                <Badge variant="secondary">×{line.qty}</Badge>
              </div>
            ))}
          </div>
        </OpSection>

        <OpSection role="optional" title="Notatka kuriera" hint="Opcjonalne. Nie blokuje etykiety.">
          <Input placeholder="np. domofon 12, zostawić u sąsiada" />
        </OpSection>

        <OpSection role="advanced" title="Debug / raw quote">
          <Button type="button" variant="ghost" size="sm">
            Pokaż JSON wyceny
          </Button>
        </OpSection>
      </div>

      <div className="space-y-4 xl:col-span-4">
        <OpStepRail
          className="xl:sticky xl:top-4"
          title="Proces paczki"
          now={jobRunning ? "Etykieta u kuriera…" : "Telefon OK · czekam na Wycena"}
          steps={jobRunning ? SHIP_STEPS : SHIP_STEPS.map((s, i) => (i >= 2 ? { ...s, state: "waiting" as const, percent: 0, detail: undefined } : s))}
        />
        <OpCallout variant="help" title="Legenda">
          Brand = w toku / CTA. Success = gotowe. Warning = sandbox. Destructive = blokada.
        </OpCallout>
      </div>
    </div>
  );
}

function AskMock() {
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <OpNowBar
        label="Napisz pytanie do zespołu, potem Wyślij"
        detail="Makieta Zapytaj zespół / Bart. Jedno pole + jeden CTA."
      />
      <OpSection role="now" step={1} title="Pytanie" hint="Główne pole rozmowy.">
        <OpPrimaryField
          label="Główne pole · pytanie"
          hint="Nie mieszaj tu filtrów i historii."
          placeholder="Np. Czy pakiet digital powinien być defaultem na landingu?"
          defaultValue=""
          className="[&_textarea]:min-h-[120px]"
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="brand" size="lg">
            <Sparkles className="mr-2 h-4 w-4" />
            Wyślij do zespołu
          </Button>
          <Button type="button" variant="outline" size="lg">
            Wyczyść
          </Button>
        </div>
      </OpSection>
      <OpSection role="optional" title="Kontekst" hint="SKU, tydzień, załączniki. Nie blokuje wysyłki.">
        <div className="flex flex-wrap gap-1.5">
          <Chip active>SKU: ks1</Chip>
          <Chip>Tydzień: bieżący</Chip>
        </div>
      </OpSection>
      <OpCallout variant="process" title="Zespół pisze list">
        Strażnik + Szef. Czekaj na werdykt.
      </OpCallout>
      <OpCallout variant="success" title="List gotowy">
        <span className="inline-flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4" />
          Otwórz list poniżej. Nie ma drugiego brand CTA w tym samym fokusie.
        </span>
      </OpCallout>
    </div>
  );
}

function LegendTab() {
  return (
    <div className="space-y-8">
      <div>
        <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Rodziny strukturalne (nie 11 równych pasteli)
        </p>
        <p className="mb-4 text-sm text-slate-600">
          Najpierw rozpoznajesz rodzinę (pasek, mono, dashed, cień, ruch), potem hue w rodzinie.
        </p>
        <div className="space-y-6">
          {OP_SURFACE_FAMILIES.map((family) => (
            <div key={family.id}>
              <div className="mb-2">
                <p className="text-sm font-semibold text-slate-900">{family.title}</p>
                <p className="text-xs text-slate-600">{family.blurb}</p>
              </div>
              <div
                className={cn(
                  "grid gap-3",
                  family.tones.length === 1 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2",
                )}
              >
                {family.tones.map((tone) => {
                  const meta = OP_SURFACE[tone];
                  return (
                    <div key={tone} className={cn("p-4", meta.box, meta.ink)}>
                      <OpRoleBadge tone={tone} size="md" />
                      <p
                        className={cn(
                          "mt-1 text-sm",
                          tone === "log"
                            ? "text-zinc-300"
                            : tone === "preview"
                              ? "text-slate-300"
                              : "text-slate-700",
                        )}
                      >
                        {meta.hint}
                      </p>
                      <p
                        className={cn(
                          "mt-2 font-mono text-[10px]",
                          tone === "log" || tone === "preview" ? "text-zinc-500" : "text-slate-500",
                        )}
                      >
                        {tone} · {meta.chrome}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">Semafor (nie mylić z rolą)</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border-2 border-success bg-success/10 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              Zieleń = Twoje światło
            </p>
            <p className="mt-1 text-sm text-slate-700">Wybrane, zatwierdzone, krok zaliczony. Nie maluj całej strefy wyniku.</p>
          </div>
          <div className={cn("rounded-xl p-4", OP_SURFACE.review.box)}>
            <p className="flex items-center gap-2 text-sm font-semibold text-orange-800">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              Pomarańcz = uwaga
            </p>
            <p className="mt-1 text-sm text-orange-950">Sprawdź zanim pójdziesz (recenzja, sandbox).</p>
          </div>
          <div className={cn("rounded-xl p-4", OP_SURFACE.danger.box)}>
            <p className="flex items-center gap-2 text-sm font-semibold text-red-800">
              <XCircle className="h-4 w-4" aria-hidden />
              Czerwień = problem
            </p>
            <p className="mt-1 text-sm text-red-950">Gate, błąd, reklamacja. Zablokowane.</p>
          </div>
        </div>
      </div>

      <div className={cn("rounded-xl p-4", OP_SURFACE.help.box)}>
        <p className="flex items-center gap-2 text-sm font-semibold text-yellow-950">
          <Package className="h-4 w-4 text-yellow-700" />
          Zasada
        </p>
        <p className="mt-1 text-sm text-yellow-950">
          Rodzina = forma (pasek / mono / dashed / cień). Hue = rola w rodzinie. Semafor = osobno. Nie maluj domeny
          zakładki.
        </p>
      </div>
    </div>
  );
}

export default function OpDemo() {
  const [mode, setMode] = useState<"loud" | "weak">("loud");
  const [draft, setDraft] = useState("Kiedyś…\nTeraz…\n1. ");
  const [jobRunning, setJobRunning] = useState(false);

  return (
    <div className="space-y-4">
      <PageHeader
        title="OVL demo"
        subtitle="Makiety: katalog, Modal AI, Przetwarzanie, Edycja slajdu, Brief, Wysyłka. Tła OVL o mocnym kontraście."
        actions={
          <Button
            type="button"
            size="sm"
            variant={jobRunning ? "brand" : "outline"}
            onClick={() => setJobRunning((v) => !v)}
          >
            {jobRunning ? "Job: ON" : "Job: off"}
          </Button>
        }
      />

      <OpCallout variant="help" title="Test 1 sekundy">
        Wskaż bez myślenia: Teraz, główne pole, CTA, opcjonalne, proces. Przełączaj zakładki poniżej.
      </OpCallout>

      <Tabs defaultValue="catalog">
        <TabsList className="flex h-auto flex-wrap gap-1">
          <TabsTrigger value="catalog">Katalog ról</TabsTrigger>
          <TabsTrigger value="modal">Modal AI</TabsTrigger>
          <TabsTrigger value="przetwarzanie">Przetwarzanie</TabsTrigger>
          <TabsTrigger value="slajd">Edycja slajdu</TabsTrigger>
          <TabsTrigger value="brief">Makieta Brief</TabsTrigger>
          <TabsTrigger value="wysylka">Makieta Wysyłka</TabsTrigger>
          <TabsTrigger value="buttons">Przyciski i pola</TabsTrigger>
          <TabsTrigger value="list">Lista i statusy</TabsTrigger>
          <TabsTrigger value="ask">Zapytaj / Bart</TabsTrigger>
          <TabsTrigger value="legend">Legenda</TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={mode === "loud" ? "brand" : "outline"}
              onClick={() => setMode("loud")}
            >
              Jest (głośne Op*)
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === "weak" ? "brand" : "outline"}
              onClick={() => setMode("weak")}
            >
              Was (słabe Studio*)
            </Button>
          </div>
          {mode === "loud" ? (
            <LoudCatalog draft={draft} setDraft={setDraft} />
          ) : (
            <WeakCatalog draft={draft} setDraft={setDraft} />
          )}
        </TabsContent>

        <TabsContent value="modal">
          <OpAiJobModalDemoTab />
        </TabsContent>

        <TabsContent value="przetwarzanie">
          <OpPrzetwarzanieDemo />
        </TabsContent>

        <TabsContent value="slajd">
          <OpSlideEditDemo />
        </TabsContent>

        <TabsContent value="brief">
          <BriefMock draft={draft} setDraft={setDraft} jobRunning={jobRunning} />
        </TabsContent>

        <TabsContent value="wysylka">
          <WysylkaMock jobRunning={jobRunning} />
        </TabsContent>

        <TabsContent value="buttons">
          <ButtonsAndFields />
        </TabsContent>

        <TabsContent value="list">
          <StatusListExamples />
        </TabsContent>

        <TabsContent value="ask">
          <AskMock />
        </TabsContent>

        <TabsContent value="legend">
          <LegendTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
